import React from "react";

export type Option<T = string | number> = { label: string; value: T };

export enum SelectSize {
  SMALL,
  MEDIUM,
  LARGE,
  XLARGE,
  XXLARGE,
}

interface SelectProps<T = string | number> {
  value: T | undefined;
  onChange: (value: T | undefined) => void;
  options: Option<T>[];
  size?: SelectSize;
  allowEmpty?: boolean;
}

const Select = <T extends string | number = string>({
  size,
  options,
  value,
  onChange,
  allowEmpty = false,
}: SelectProps<T>) => {
  const selectSize =
    size === SelectSize.SMALL
      ? "w-10"
      : size === SelectSize.MEDIUM
      ? "w-20"
      : size === SelectSize.LARGE
      ? "w-32"
      : size === SelectSize.XLARGE
      ? "w-40"
      : size === SelectSize.XXLARGE
      ? "w-48"
      : "w-full";

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const stringValue = e.target.value;

    // Handle empty selection
    if (stringValue === "" || stringValue === "undefined") {
      onChange(undefined);
      return;
    }

    if (typeof value === "number" || (options.length > 0 && typeof options[0].value === "number")) {
      onChange(Number(stringValue) as T);
    } else {
      onChange(stringValue as T);
    }
  };

  return (
    <select
      className={`${selectSize} relative font-semibold text-center justify-center appearance-none rounded-md border border-gray-300 bg-white p-2 text-gray-700 shadow-sm transition-all`}
      value={value ?? ""}
      onChange={handleChange}
    >
      {allowEmpty && (
        <option key={-1} value="">
          -- Choose an option --
        </option>
      )}

      {options?.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
};

export default Select;
